import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { collection, query, orderBy, onSnapshot, doc, updateDoc, getDoc, serverTimestamp, addDoc } from "firebase/firestore";import { auth, db } from "../../firebase";
import { useCompany } from "../../context/CompanyContext";
import { useLeads } from "../../context/LeadsContext";
import { sanitizeForFirestore } from "../../utils/firestoreUtils";
import MessageBubble from "./MessageBubble";
import ChatInput from "./ChatInput";
import QuickQuoteModal from "./QuickQuoteModal";
import LeadContextStrip from "./LeadContextStrip";
import CallButton from "../clicktocall/CallButton";
import { Loader2, MessageCircle, Phone, FileText, UserPlus, Info, AlertCircle, CheckCircle2, RotateCcw } from "lucide-react";
import { formatPhone } from "../../utils/phoneUtils";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

import { avatarColor as _avatarColor, avatarInitials as _initials } from './avatarUtils';

// ─────────────────────────────────────────────────────────────────────────────

const ChatWindow = ({ conversation, hideMobileHeader, pendingQuote, onQuoteSent, onToggleDetails, showDetails, onResolve }) => {
  const { companyId } = useCompany();
  const { leadsMap } = useLeads();
  const conversationLead = conversation?.leadId ? leadsMap?.[conversation.leadId] : null;
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [quotePrompt, setQuotePrompt] = useState(null);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const [quickQuoteOpen, setQuickQuoteOpen] = useState(false);
  const [isOptedOut, setIsOptedOut] = useState(false);
  const [resolving, setResolving] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const initialScrollDoneRef = useRef(false);

  const isResolved = conversation?.status === 'resolved';

  const scrollToBottom = (behavior = "smooth") => {
    if (behavior === "auto" && messagesContainerRef.current) {
      // Instant jump — set scrollTop directly so no animation can occur
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      return;
    }
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  // When a voicemail "Reply" CTA is tapped in MessageBubble, scroll the
  // thread to the composer so the user sees the prefilled input. The
  // composer itself is handled by ChatInput (listens to the same event
  // and focuses / prefills).
  useEffect(() => {
    const onReply = () => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    };
    window.addEventListener('kro:voicemail-reply', onReply);
    return () => window.removeEventListener('kro:voicemail-reply', onReply);
  }, []);

  const handleResolve = async () => {
    if (!companyId || !conversation?.id || resolving) return;
    setResolving(true);
    try {
      await updateDoc(
        doc(db, 'companies', companyId, 'conversations', conversation.id),
        {
          status: 'resolved',
          resolvedAt: serverTimestamp(),
          resolvedBy: auth?.currentUser?.uid || null,
        }
      );
      onResolve?.(conversation.id);
    } catch (err) {
      console.error('[ChatWindow] Failed to resolve conversation', err);
    } finally {
      setResolving(false);
    }
  };

  const handleReopen = async () => {
    if (!companyId || !conversation?.id || resolving) return;
    setResolving(true);
    try {
      await updateDoc(
        doc(db, 'companies', companyId, 'conversations', conversation.id),
        { status: 'open', reopenedAt: serverTimestamp() }
      );
      // onResolve clears the selection — let parent handle it
      onResolve?.(conversation.id);
    } catch (err) {
      console.error('[ChatWindow] Failed to reopen conversation', err);
    } finally {
      setResolving(false);
    }
  };

  // Load current user profile (for avatar display)
  useEffect(() => {
    const loadUserProfile = async () => {
      const currentUser = auth?.currentUser;
      if (!currentUser) return;

      try {
        // Try to get photoURL from Firestore users collection first
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const data = userDoc.data();
          setCurrentUserProfile({
            uid: currentUser.uid,
            displayName: data.displayName || currentUser.displayName || '',
            photoURL: data.photoURL || currentUser.photoURL || null,
          });
        } else {
          // Fall back to Firebase Auth data
          setCurrentUserProfile({
            uid: currentUser.uid,
            displayName: currentUser.displayName || '',
            photoURL: currentUser.photoURL || null,
          });
        }
      } catch (err) {
        // On error, use Firebase Auth data
        setCurrentUserProfile({
          uid: currentUser.uid,
          displayName: currentUser.displayName || '',
          photoURL: currentUser.photoURL || null,
        });
      }
    };

    loadUserProfile();
  }, []);

  // Reset initial-positioning flag whenever a different thread is opened
  useLayoutEffect(() => {
    initialScrollDoneRef.current = false;
  }, [conversation?.id]);

  // Position the thread: instant jump (pre-paint) on initial load,
  // smooth scroll only for messages arriving after the thread is open.
  useLayoutEffect(() => {
    if (messages.length === 0) return;
    if (!initialScrollDoneRef.current) {
      scrollToBottom("auto");
      initialScrollDoneRef.current = true;
    } else {
      scrollToBottom("smooth");
    }
  }, [messages]);

  // Mark conversation as read when opened
  useEffect(() => {
    if (conversation?.id && db && companyId) {
      const markAsRead = async () => {
        try {
          await updateDoc(
            doc(db, "companies", companyId, "conversations", conversation.id),
            sanitizeForFirestore({
              unreadCount: 0,
              lastMessageRead: true,
              lastReadAt: serverTimestamp(),
            })
          );
        } catch (error) {
          console.error("Error marking conversation as read:", error);
        }
      };
      markAsRead();
    }
  }, [companyId, conversation?.id]);

  // Load messages from Firestore
  useEffect(() => {
    if (!conversation?.id || !db || !companyId) {
      setMessages([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const q = query(
        collection(db, "companies", companyId, "conversations", conversation.id, "messages"),
        orderBy("timestamp", "asc")
      );

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const msgs = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setMessages(msgs);
          setLoading(false);
        },
        () => {
          setMessages([]);
          setLoading(false);
        }
      );

      return () => unsubscribe();
    } catch (err) {
      setMessages([]);
      setLoading(false);
    }
  }, [companyId, conversation]);

  // Handle pending quote from Calculator handoff
  useEffect(() => {
    if (!pendingQuote?.quoteId || !pendingQuote?.conversation || pendingQuote.noConversation) {
      return;
    }

    // Prepare the quote for sending (creates sendable copy with publicUrl)
    const prepareQuote = async () => {
      try {
        const currentUser = auth?.currentUser;
        if (!currentUser) return;

        const token = await currentUser.getIdToken(true);
        const resp = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/quotes/prepare-send`,
          {
            quoteId: pendingQuote.quoteId,
            leadId: pendingQuote.leadId,
            conversationId: conversation?.id,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (resp.data?.success && resp.data?.publicUrl) {
          // Set the quote prompt to show send banner
          setQuotePrompt({
            quoteId: resp.data.quoteId,
            total: resp.data.total,
            publicUrl: resp.data.publicUrl,
          });
        }
      } catch (err) {
        console.error('Failed to prepare quote for sending:', err);
      }
    };

    prepareQuote();
  }, [pendingQuote, conversation?.id]);

  const handleSendMessage = async (text) => {
    if (!text.trim()) return;

    if (!conversation?.clientPhone) {
      console.error("Missing conversation.clientPhone — cannot send SMS");
      return;
    }

    setSending(true);
    try {
      const payload = {
        to: conversation.clientPhone,
        message: text,
        conversationId: conversation.id,
      };

      const currentUser = auth?.currentUser;
      if (!currentUser) {
        console.error("SEND ERROR: No authenticated user for outbound SMS");
        return;
      }

      const token = await currentUser.getIdToken(true);

      const resp = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/sms/send`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Message is always written to Firestore and will appear via onSnapshot.
      // Show a toast only if provider delivery failed so the agent is aware.
      if (resp.data?.deliveryStatus === 'failed') {
        toast.error('Message saved but could not be delivered — SMS provider temporarily unavailable.');
      }
    } catch (err) {
      console.error("SEND ERROR:", err);
      if (err.response) {
        console.error("Backend Response:", err.response.data);
      }
      toast.error('Failed to send message. Please check your connection and try again.');
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    const handler = (event) => {
      if (!event.detail) return;
      const { quoteId, total, publicUrl, conversationId } = event.detail;
      if (!conversation?.id || conversation.id !== conversationId) return;
      setQuotePrompt({ quoteId, total, publicUrl });
    };

    window.addEventListener('inbox:quote-created', handler);
    return () => window.removeEventListener('inbox:quote-created', handler);
  }, [conversation?.id]);

  // ── Opt-out listener ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!conversation?.clientPhone || !companyId || !db) {
      setIsOptedOut(false);
      return;
    }
    const phoneKey = conversation.clientPhone.replace(/\D/g, '');
    const optOutRef = doc(db, 'companies', companyId, 'sms_opt_outs', phoneKey);
    const unsub = onSnapshot(optOutRef, (snap) => {
      setIsOptedOut(snap.exists() && snap.data()?.optedOut === true);
    }, () => setIsOptedOut(false));
    return () => unsub();
  }, [conversation?.clientPhone, companyId]);

  const handleSendQuoteSms = async () => {
    if (!quotePrompt || !conversation?.clientPhone) return;
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    try {
      const token = await currentUser.getIdToken(true);
      // publicUrl is now a full URL from backend - do not prepend anything
      const text = `Here’s your quote for $${(quotePrompt.total || 0).toFixed(2)}: ${quotePrompt.publicUrl}`;
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/sms/send`,
        {
          to: conversation.clientPhone,
          message: text,
          conversationId: conversation.id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Write system message: quote_sent
      // Store the full publicUrl so MessageBubble can use it directly
      try {
        if (db && companyId && conversation?.id) {
          await addDoc(
            collection(
              db,
              'companies',
              companyId,
              'conversations',
              conversation.id,
              'messages'
            ),
            {
              type: 'internal',
              kind: 'quote_sent',
              quoteId: quotePrompt.quoteId,
              publicUrl: quotePrompt.publicUrl,  // Store full URL for future reference
              total: quotePrompt.total,
              from: 'system',
              timestamp: serverTimestamp(),
            }
          );
        }
      } catch (e) {
        console.error('Failed to write quote_sent system message', e);
      }

      setQuotePrompt(null);
      
      // Notify parent that quote was sent (for Calculator handoff flow)
      if (onQuoteSent) {
        onQuoteSent();
      }
    } catch (err) {
      console.error('Failed to send quote SMS', err);
    }
  };

  const handleCopyQuoteLink = async () => {
    if (!quotePrompt?.publicUrl) return;
    try {
      await navigator.clipboard.writeText(`${quotePrompt.publicUrl}`);
    } catch (err) {
      console.error('Failed to copy quote link', err);
    }
  };

  // No conversation selected
  if (!conversation?.id) {
    return (
      <div className="flex flex-col h-full">
        {quotePrompt && (
          <div className="border-t border-gray-200 bg-white px-4 py-3 flex items-center justify-between text-xs text-gray-700">
            <div>
              <span className="font-medium">Send this quote to the customer?</span>
              <span className="ml-1 text-gray-500">
                Total ${quotePrompt.total?.toFixed(2) || '0.00'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleSendQuoteSms}
                className="px-2 py-1 rounded-md bg-[#0A2647] text-white hover:bg-[#0A2647]/90"
              >
                Send via SMS
              </button>
              <button
                type="button"
                onClick={handleCopyQuoteLink}
                className="px-2 py-1 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Copy link
              </button>
              <button
                type="button"
                onClick={() => setQuotePrompt(null)}
                className="px-2 py-1 text-gray-400 hover:text-gray-600"
              >
                Not now
              </button>
            </div>
          </div>
        )}

        <div className="flex flex-col items-center justify-center h-full bg-gray-50">
          <MessageCircle className="h-16 w-16 text-gray-400 mb-4" />
          <p className="text-sm font-medium text-gray-900 mb-1">
            Select a conversation
          </p>
          <p className="text-xs text-gray-500">
            Choose a conversation from the list to view messages
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="border-b border-gray-200 px-5 py-3 bg-white shadow-sm relative z-10 flex-shrink-0">
        <div className="flex items-center justify-between gap-3">

          {/* Left: avatar + name + phone */}
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center text-white text-sm font-semibold shadow-sm"
              style={{ backgroundColor: _avatarColor(conversation.displayName || conversation.clientName || conversation.clientPhone || '') }}
            >
              {_initials(conversation.displayName || conversation.clientName || conversation.clientPhone || '')}
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-gray-900 leading-tight truncate">
                {conversation.displayName || conversation.clientName || formatPhone(conversation.clientPhone) || conversation.clientPhone}
              </h2>
              <p className="text-xs text-gray-400 leading-tight truncate">
                {formatPhone(conversation.clientPhone) || conversation.clientPhone}
              </p>
            </div>
          </div>

          {/* Right: quick actions + info toggle */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Resolve / Re-open */}
            {!isResolved ? (
              <button
                type="button"
                title="Mark as resolved"
                data-testid="header-resolve-btn"
                onClick={handleResolve}
                disabled={resolving}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium
                  text-gray-500 hover:bg-green-50 hover:text-green-700 transition-colors disabled:opacity-50"
              >
                {resolving
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <CheckCircle2 className="w-3.5 h-3.5" />
                }
                <span className="hidden sm:inline">Resolve</span>
              </button>
            ) : (
              <button
                type="button"
                title="Re-open conversation"
                data-testid="header-reopen-btn"
                onClick={handleReopen}
                disabled={resolving}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium
                  text-amber-600 bg-amber-50 hover:bg-amber-100 transition-colors disabled:opacity-50"
              >
                {resolving
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <RotateCcw className="w-3.5 h-3.5" />
                }
                <span className="hidden sm:inline">Re-open</span>
              </button>
            )}

            {/* Call (Click-to-Call — Phase 3.1A) */}
            {conversation.clientPhone && (
              <CallButton
                phone={conversation.clientPhone}
                conversationId={conversation.id}
                surface="inbox-header"
                title="Call customer"
                data-testid="header-call-btn"
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors"
              >
                <Phone className="w-4 h-4" />
              </CallButton>
            )}

            {/* Create Quote */}
            <button
              type="button"
              title="Create quote"
              data-testid="header-create-quote-btn"
              onClick={() => setQuickQuoteOpen(true)}
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors"
            >
              <FileText className="w-4 h-4" />
            </button>

            {/* Create Lead */}
            <button
              type="button"
              title="Create lead"
              data-testid="header-create-lead-btn"
              onClick={() => navigate('/leads')}
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors"
            >
              <UserPlus className="w-4 h-4" />
            </button>

            {/* Info / Details toggle */}
            {onToggleDetails && (
              <button
                type="button"
                title={showDetails ? 'Hide details' : 'Show details'}
                data-testid="header-toggle-details-btn"
                onClick={onToggleDetails}
                className={`p-2 rounded-lg transition-colors ${
                  showDetails
                    ? 'bg-[#0f4c81]/10 text-[#0f4c81]'
                    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800'
                }`}
              >
                <Info className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── B1: Lead context strip ─────────────────────────────────────────── */}
      <LeadContextStrip conversation={conversation} />

      {/* Quick Quote Modal */}
      <QuickQuoteModal
        open={quickQuoteOpen}
        onClose={() => setQuickQuoteOpen(false)}
        conversation={conversation}
        companyId={companyId}
        onQuoteCreated={(info) => {
          setQuickQuoteOpen(false);
          if (info && conversation?.id) {
            const event = new CustomEvent('inbox:quote-created', {
              detail: { ...info, conversationId: conversation.id },
            });
            window.dispatchEvent(event);
          }
        }}
      />

      {/* Messages Area */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-6 pt-4 bg-gray-50 relative z-0">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm text-gray-500">No messages yet</p>
          </div>
        ) : (
          <div className="flex flex-col space-y-3">
            {messages.map((message) => (
              <MessageBubble 
                key={message.id} 
                message={message} 
                currentUser={currentUserProfile}
                conversationId={conversation.id}
                clientPhone={conversation.clientPhone}
              />
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Quote prompt */}
      {quotePrompt && (
        <div className="border-t border-gray-200 bg-white px-4 py-3 flex items-center justify-between text-xs text-gray-700">
          <div>
            <span className="font-medium">Send this quote to the customer?</span>
            <span className="ml-1 text-gray-500">
              Total ${quotePrompt.total?.toFixed(2) || '0.00'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSendQuoteSms}
              className="px-2 py-1 rounded-md bg-[#0A2647] text-white hover:bg-[#0A2647]/90"
            >
              Send via SMS
            </button>
            <button
              type="button"
              onClick={handleCopyQuoteLink}
              className="px-2 py-1 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Copy link
            </button>
            <button
              type="button"
              onClick={() => setQuotePrompt(null)}
              className="px-2 py-1 text-gray-400 hover:text-gray-600"
            >
              Not now
            </button>
          </div>
        </div>
      )}

      {/* Opt-out banner */}
      {isOptedOut && (
        <div className="mx-4 mb-2 flex items-start gap-2.5 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs" data-testid="opted-out-banner">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-500" />
          <span>
            This contact has opted out of SMS. They must reply <strong>START</strong> to receive messages.
          </span>
        </div>
      )}

      {/* Chat input */}
      <ChatInput onSend={handleSendMessage} disabled={sending} optedOut={isOptedOut} lead={conversationLead} />
    </div>
  );
};

export default ChatWindow;
